/**
 * Sign in.
 *
 * On the mock build the accounts are listed in the open, because hiding them would only mean
 * nobody could get in. Against the real API that panel disappears (API_SOURCE !== 'mock'),
 * and the endpoint does the rate limiting the plan specifies.
 *
 * Author: Danish Khan
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { API_SOURCE } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { useTheme } from '../lib/themeProvider.tsx';
import { Button } from '../components/Button.tsx';

const DEMO_ACCOUNTS = [
  { email: 'ritu@maincharacterglobal.com', role: 'manager', note: 'sees everything' },
  { email: 'karthik@maincharacterglobal.com', role: 'rep', note: 'no Explain panel, no People' },
  { email: 'ops@example.com', role: 'ops', note: 'Explain yes, phone numbers redacted' },
  { email: 'kavisha@maincharacterglobal.com', role: 'owner', note: 'sees everything' },
];

export function LoginPage(): ReactNode {
  const { session, signIn, loading } = useSession();
  const { mode, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // Prefilled only on the mock build, where the accounts are listed in the open anyway. Against
  // the real API a prefilled address is a wrong guess somebody has to clear first.
  const [email, setEmail] = useState(API_SOURCE === 'mock' ? 'ritu@maincharacterglobal.com' : '');
  const [password, setPassword] = useState(API_SOURCE === 'mock' ? 'demo' : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && session) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-bg px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden
              className="flex size-9 items-center justify-center rounded-[var(--h1-radius)] bg-brand text-sm font-bold text-brand-fg"
            >
              H1
            </span>
            <div>
              <p className="text-sm font-semibold text-fg">H1 Console</p>
              <p className="text-xs text-fg-muted">Main Character</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={toggle} aria-label={`Switch to ${mode === 'dark' ? 'light' : 'dark'} theme`}>
            <span aria-hidden>{mode === 'dark' ? '☀' : '☾'}</span>
          </Button>
        </div>

        <form onSubmit={(e) => void submit(e)} className="h1-card flex flex-col gap-3 p-5">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-fg">Email</span>
            {/*
              `type="text"`, not `type="email"`. The browser's own email validation is an
              ASCII pattern and would refuse a Devanagari local part or an internationalised
              domain — which is precisely the check the API deliberately does NOT do. A field
              that refuses an address the server would have accepted is the client lying about
              the rule.
            */}
            <input
              type="text"
              inputMode="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-9 rounded-[var(--h1-radius)] border border-line-strong bg-surface px-3 text-sm text-fg"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-fg">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-9 rounded-[var(--h1-radius)] border border-line-strong bg-surface px-3 text-sm text-fg"
            />
          </label>

          {error && (
            <p role="alert" className="rounded-md bg-danger-soft px-2.5 py-2 text-xs text-danger-soft-fg">
              {error}
            </p>
          )}

          <Button type="submit" disabled={busy} className="mt-1">
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        {API_SOURCE === 'mock' && (
          <section className="mt-4 rounded-[var(--h1-radius)] border border-dashed border-line-strong p-3">
            <p className="text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
              Mock build — password is <span className="font-mono text-brand-ink">demo</span>
            </p>
            <ul className="mt-2 flex flex-col gap-1.5">
              {DEMO_ACCOUNTS.map((a) => (
                <li key={a.email}>
                  <button
                    type="button"
                    onClick={() => {
                      setEmail(a.email);
                      setPassword('demo');
                    }}
                    className="w-full rounded-md px-2 py-1 text-left text-[11px] hover:bg-surface-2"
                  >
                    <span className="font-mono text-fg">{a.email}</span>
                    <span className="block text-fg-subtle">
                      {a.role} — {a.note}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
